/**
 * "Insert a data reference" beside a DSL field — the host ADR-127's picker never had.
 *
 * `DataReferencePicker` has been complete and tested since the D9-D15 work, with ZERO
 * importers: nothing in the editor offered a way to open it, so the only way to put a
 * `{{data.…}}` reference into a workflow was to type it from memory — which is the habit the
 * whole reference-data programme exists to remove. This is the affordance that closes that,
 * and it deliberately holds no knowledge of its own.
 *
 * WHY IT WRAPS AN UNCONTROLLED FIELD INSTEAD OF REPLACING IT
 * ---------------------------------------------------------
 * The task-field form is uncontrolled — `defaultValue` + `onBlur` — because a workflow draft
 * is large and re-rendering the whole tree per keystroke was not worth it. Converting the
 * field to controlled to support insertion would change the editing behaviour of every field
 * around it for one feature's benefit. So insertion goes through a ref: read the live value,
 * append the token at the caret, write it back, and report the new value through the SAME
 * `onChange` the blur handler uses. The field's own semantics are untouched.
 *
 * WHY IT APPENDS AT THE CARET AND NOT AT THE END
 * ---------------------------------------------
 * `agent.run` is a paragraph of prose. A reference belongs in the sentence being written, and
 * an operator who has to cut and paste the token into place is doing the editing this control
 * exists to avoid.
 *
 * WHY A HINT IS NOT A SECOND PICKER (ADR-179)
 * -------------------------------------------
 * A literal that already equals the resolved value of a published reference is not a naming
 * question, it is a click. The host hands this component the matches for THIS field, computed
 * by the canonical matcher, and the hint opens the same picker over just those candidates. The
 * component never compares values, never parses the field's display string, and never decides
 * what a location is: it renders what it was given and reports the selection by location.
 *
 * WHAT IT DOES NOT DO
 * ------------------
 * It never builds a token. Every token comes from the canonical offers endpoint, verbatim
 * (D15: "any reference the picker can construct MUST pass validation… a failure is a defect in
 * the picker, never operator error"). This component only decides WHERE the text lands.
 */

import { useRef, useState, type ReactNode } from 'react'

import { api } from '../api/client'
import type {
  DataReferenceMatch, DataReferenceMatchLocation, VarsOffer,
} from '../api/client'
import { DataReferencePicker } from './DataReferencePicker'
import { insertAtCaret } from './insertAtCaret'
import { sameLocation } from './useMatches'
import type { DataReferenceSelection } from './workflowDraft'

/** ADR-179: a `true`, a number or a one-character string equals something in some set often
 *  enough that volunteering a hint for it would be noise on every field. */
const MIN_HINT_LITERAL_LENGTH = 2

export interface DataRefFieldAffordanceProps {
  /** The DSL slot being edited. Decides which offers exist AND which syntax they use, so it
   *  must be the real slot — passing a convenient lookalike would serve offers shaped for a
   *  site the value is not going into. */
  slot: string
  /** In-scope runtime operands for a `by_key[...]` index, when the caller knows them. */
  operands?: string[]
  /** Called with the field's FULL new value after an insertion. */
  onChange: (value: string) => void
  /**
   * Establish the `data:` pin the token needs, BEFORE it is inserted.
   *
   * Return a refusal string to abort the insertion — the host is the only thing
   * that knows whether the alias is already bound to a different set, and inserting
   * a token whose pin could not be created would leave a reference that fails
   * validation with no explanation on screen.
   *
   * Optional so a host with no document model (a preview, a test) still works; when
   * absent the token is inserted as before, which is the pre-2026-08-11 behaviour.
   */
  onEnsurePin?: (pin: { alias: string; set: string }) => string | null | void
  /**
   * Distinguishes SEVERAL affordances on the same slot, for the test id only.
   *
   * A mapping site (`set`, `python.inputs`, `run.inputs`) renders one of these PER ROW, all
   * on the same slot — so the id has to carry which row, or every assertion and every click
   * would land on whichever one the DOM happened to yield first.
   */
  instance?: string
  /**
   * ADR-153 D3 — the draft and the selected task, enabling a SECOND source of
   * insertable values: workflow-locals a `set` has bound.
   *
   * A separate button and a separate list, deliberately. `{{data.*}}` is the
   * versioned registry — shared across workflows, with provenance, a freshness
   * contract and a required pin. A `{{vars.*}}` lives for one run and has none
   * of that. Offering them as one list would make a pinned reference and a
   * run-scoped scratch value look interchangeable at the point of use, which is
   * the confusion ADR-153 D2 exists to prevent.
   *
   * Absent, no workflow-value button renders — a host with no draft (a preview,
   * a test) is unaffected.
   */
  varsContext?: { draft: string; task: string }
  /**
   * Where in the parsed draft this field's value lives, so a selection can be reported
   * without anyone parsing the DOM. `command.args` is displayed comma-joined
   * (`Workflows.tsx`), so the field's text is not the value at any one index — only the
   * per-match locations below address those.
   *
   * Absent, no selection can be located: the caret path falls back to its
   * `onEnsurePin` + `onChange` behaviour and no hint renders.
   */
  location?: DataReferenceMatchLocation
  /**
   * The matcher's findings for this field — one entry per literal site it covers. A
   * `command.args` field has one per index; `agent.run` and a mapping row have at most one.
   *
   * Only string literals of `MIN_HINT_LITERAL_LENGTH` characters or more get a hint. The rest
   * are still swept by the activity-level panel, where the operator went looking for them.
   */
  matches?: DataReferenceMatch[]
  /**
   * The value the draft holds for this field, as the host rendered it into `defaultValue`.
   *
   * The field is uncontrolled, so a value typed and not yet blurred lives only in the DOM.
   * Replacing a literal addressed in the PARSED draft would silently discard that typing, so
   * the hint refuses while the two disagree rather than choosing which one wins.
   */
  draftValue?: string
  /**
   * Report a picked reference — the token AND its pin, as one selection the host applies in
   * a single draft mutation.
   *
   * Supersedes `onEnsurePin` + `onChange` for BOTH the hint and the caret path when present:
   * those two callbacks close over the same draft in the host, so applying them in sequence
   * loses the first write. Absent, both paths behave exactly as before.
   */
  onApplyReferences?: (selections: DataReferenceSelection[]) => void
  /**
   * Turn this field's DISPLAY text into the value the draft holds at `location`, for a host
   * whose field is not the value.
   *
   * `command.args` is the whole array comma-joined into one input, so a caret offset in it
   * addresses no single index and the token cannot be spliced in the parsed draft. The host
   * owns that translation already (it re-splits on blur); handing it over here lets the
   * caret path write the field AND its pin in one mutation, which the sequential
   * `onEnsurePin` + `onChange` pair could not do (ADR-179 design decision 10).
   *
   * Absent, the caret path splices at `location` directly.
   */
  encodeFieldValue?: (text: string) => unknown
  /** The character(s) `encodeFieldValue` splits the display text on. A caret insert adds no
   *  padding before one of them, because that space would land inside the entry. */
  entryDelimiter?: string
  /** The field itself. Receives the ref the affordance reads and writes through. */
  children: (ref: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>) => ReactNode
}

/** The part of a test id that separates several hints on ONE field: `command.args` renders
 *  the whole array in one textarea, so the index is what tells two hints apart. */
function locationSuffix(loc: DataReferenceMatchLocation): string {
  if (typeof loc.index === 'number') return `-${loc.index}`
  if (typeof loc.key === 'string') return `-${loc.key}`
  return ''
}

/** The address of the site a hint would change, for the hint's title: `args[3]`, or the
 *  mapping key. Null when the field IS the site and the address adds nothing. */
function locationLabel(loc: DataReferenceMatchLocation): string | null {
  if (typeof loc.index === 'number') return `${loc.slot.split('.').pop()}[${loc.index}]`
  if (typeof loc.key === 'string') return loc.key
  return null
}

/** Longest literal a hint shows before trimming. Enough to recognise a Drive folder id or a
 *  file prefix at a glance; the whole value stays in the hint's title. */
const HINT_LITERAL_MAX_LENGTH = 24

/** What a hint shows: the literal it would replace, quoted. On a comma-joined field the
 *  operator recognises the value, not its position, so the index moved to the title. */
function hintLiteral(literal: string): string {
  const shown = literal.length > HINT_LITERAL_MAX_LENGTH
    ? `${literal.slice(0, HINT_LITERAL_MAX_LENGTH - 1)}…`
    : literal
  return `"${shown}"`
}

export function DataRefFieldAffordance({
  slot, operands, onChange, onEnsurePin, instance, varsContext,
  location, matches, draftValue, onApplyReferences, encodeFieldValue, entryDelimiter, children,
}: DataRefFieldAffordanceProps) {
  const ref = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null)
  const [open, setOpen] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [varsOpen, setVarsOpen] = useState(false)
  const [varsOffers, setVarsOffers] = useState<VarsOffer[] | null>(null)
  const [varsNote, setVarsNote] = useState<string | null>(null)
  const [hint, setHint] = useState<DataReferenceMatch | null>(null)

  /** Write `token` into the field at the caret. The pin path is data-reference
   *  only — a workflow-local has nothing to pin, which is the whole distinction. */
  const insertToken = (token: string) => {
    const el = ref.current
    if (!el) return
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? start
    const next = insertAtCaret(el.value, token, start, end, { delimiters: entryDelimiter })
    el.value = next.value
    onChange(next.value)
    el.focus()
    try {
      el.setSelectionRange(next.caret, next.caret)
    } catch {
      // Selection ranges are a nicety; the text landed.
    }
  }

  const openVars = () => {
    if (varsOpen) {
      setVarsOpen(false)
      return
    }
    setVarsOpen(true)
    setVarsOffers(null)
    setVarsNote(null)
    if (!varsContext) return
    api
      .varsOffers(varsContext.draft, varsContext.task, slot)
      .then(res => {
        setVarsOffers(res.offers)
        // A note means "could not look", which is a different fact from "binds
        // nothing" — pass it through rather than rendering emptiness as an answer.
        setVarsNote(res.note ?? null)
      })
      .catch(() => setVarsNote('Could not load workflow values.'))
  }

  const idSuffix = instance === undefined ? '' : `-${instance}`

  // Only where a candidate exists, and only where the host can actually apply one — a hint
  // whose Insert did nothing would be worse than no hint at all.
  const hints = !onApplyReferences ? [] : (matches ?? []).filter(
    m => m.candidates.length > 0 && m.type === 'string'
      && typeof m.literal === 'string' && m.literal.length >= MIN_HINT_LITERAL_LENGTH)

  /** The field's unblurred text is not in the parsed draft, and every write below is
   *  addressed there. Refuse rather than choose which of the two versions wins. */
  const hasUnsavedEdits = () =>
    draftValue !== undefined && !!ref.current && ref.current.value !== draftValue
  const UNSAVED = 'This field has unsaved edits. Save them first, then use the reference.'

  const openHint = (match: DataReferenceMatch) => {
    // By VALUE: a refetch hands the host a new matches object, so the open hint and the
    // clicked one are never the same object even when they name the same site.
    if (hint && sameLocation(hint.location, match.location)) {
      setHint(null)
      return
    }
    // The replacement is addressed in the parsed draft; unblurred typing is not in it yet.
    if (hasUnsavedEdits()) {
      setRefusal(UNSAVED)
      setHint(null)
      return
    }
    setRefusal(null)
    setOpen(false)
    setHint(match)
  }

  /** Insert from the hint: the candidate replaces the literal at its own location, so the
   *  field text is never read and the pin travels with it in one selection.
   *
   *  The candidate is found by token AND pin. Two versions of one set carry the same alias,
   *  so they render the same token, and matching on the token alone silently applied the
   *  first version whichever one the operator chose — a reference to a version they never
   *  picked, which verification accepts because the other version is valid too. */
  const insertCandidate = (token: string, pin: { alias: string; set: string }) => {
    const match = hint
    setHint(null)
    if (!match || !onApplyReferences) return
    const candidate = match.candidates.find(
      c => c.token === token && c.pin.alias === pin.alias && c.pin.set === pin.set)
    if (!candidate) return
    onApplyReferences([
      { kind: 'replace', location: match.location, literal: match.literal, candidate },
    ])
  }

  const insert = (token: string, pin: { alias: string; set: string }) => {
    if (onApplyReferences && location) {
      // The caret offset is read off the DOM but the splice happens in the PARSED draft, so
      // the two texts have to be the same one. The hint path checks this; without the same
      // check here a token lands at an offset computed for text that is not in the draft.
      if (hasUnsavedEdits()) {
        setRefusal(UNSAVED)
        setOpen(false)
        return
      }
      // One selection, one mutation: the pin and the spliced value land together, which the
      // sequential `onEnsurePin` + `onChange` pair could not do. The host re-renders the
      // field from the draft it wrote, so nothing is written into the DOM here.
      const el = ref.current
      const start = el ? el.selectionStart ?? el.value.length : 0
      const end = el ? el.selectionEnd ?? start : 0
      setRefusal(null)
      setOpen(false)
      if (encodeFieldValue) {
        // The field is not the value (a comma-joined array): splice the DISPLAY text and let
        // the host turn the result back into what the draft holds.
        const text = el ? el.value : draftValue ?? ''
        const next = insertAtCaret(text, token, start, end, { delimiters: entryDelimiter })
        onApplyReferences([
          { kind: 'write-field', location, value: encodeFieldValue(next.value), pin },
        ])
        return
      }
      onApplyReferences([{ kind: 'insert', location, token, pin, range: { start, end } }])
      return
    }
    // The pin FIRST: a token whose pin could not be established is a reference
    // that fails validation, so it must not reach the field at all.
    const problem = onEnsurePin?.(pin)
    if (typeof problem === 'string' && problem) {
      setRefusal(problem)
      setOpen(false)
      return
    }
    setRefusal(null)
    // `insertToken` also reports the new value through `onChange`, because the blur handler is
    // what normally reports a change and an insertion is not a blur. With no field to write
    // into it does nothing, and closing is the honest outcome.
    insertToken(token)
    setOpen(false)
  }

  return (
    <div>
      {children(ref)}
      <div className="mt-1">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          data-testid={`dataref-open-${slot}${idSuffix}`}
          className="text-xs text-indigo-700 hover:underline"
          title="Insert a reference to versioned reference data instead of typing the value (ADR-126)"
        >
          {open ? 'Close' : '{{ }} Insert data reference'}
        </button>
        {varsContext && (
          <button
            type="button"
            onClick={openVars}
            data-testid={`varsref-open-${slot}${idSuffix}`}
            className="ml-3 text-xs text-emerald-700 hover:underline"
            title="Insert a value a `set` task bound earlier in this workflow (ADR-153). Not reference data: it lives for one run."
          >
            {varsOpen ? 'Close' : '↳ Insert workflow value'}
          </button>
        )}
      </div>
      {hints.length > 0 && (
        // One row per hint, under the actions rather than inline with them: at the inspector's
        // width two inline hints wrapped mid-label and read as one broken sentence. The literal
        // sits left because it is what the operator recognises; the action sits right.
        <ul className="mt-1 space-y-0.5" data-testid={`dataref-hints-${slot}${idSuffix}`}>
          {hints.map(match => {
            const literal = match.literal as string
            const address = locationLabel(match.location)
            const active = !!hint && sameLocation(hint.location, match.location)
            return (
              <li key={`${match.location.slot}${locationSuffix(match.location)}`}>
                <button
                  type="button"
                  onClick={() => openHint(match)}
                  aria-expanded={active}
                  data-testid={`dataref-hint-${slot}${idSuffix}${locationSuffix(match.location)}`}
                  className={`flex w-full items-baseline justify-between gap-2 rounded px-1 py-0.5 text-left text-xs hover:bg-indigo-50 ${active ? 'bg-indigo-50' : ''}`}
                  title={`${address ? `${address}: ` : ''}"${literal}" is already published as reference data. Use the reference instead of the literal (ADR-179).`}
                >
                  <span className="min-w-0 truncate font-mono text-gray-800">
                    <span className="text-gray-400">⤷ </span>{hintLiteral(literal)}
                  </span>
                  <span className="shrink-0 text-indigo-700 hover:underline">Use data reference</span>
                </button>
                {active && hint && (
                  // Inside the row it belongs to, so the picker is visibly about THIS literal
                  // and not the field as a whole; the row above stays highlighted while open.
                  <div className="mt-1 border border-indigo-100 rounded p-2 bg-indigo-50/40"
                       data-testid={`dataref-hint-panel-${slot}${idSuffix}${locationSuffix(hint.location)}`}>
                    <DataReferencePicker
                      slot={hint.location.slot}
                      candidates={hint.candidates}
                      onInsert={insertCandidate}
                      onCancel={() => setHint(null)}
                      insertLabel={`Replace ${hintLiteral(literal)}`}
                    />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {refusal && (
        <p className="mt-1 text-xs text-amber-800"
           data-testid={`dataref-pin-refused${idSuffix}`}>
          {refusal}
        </p>
      )}
      {open && (
        <div className="mt-1 border border-indigo-100 rounded p-2 bg-indigo-50/40">
          <DataReferencePicker
            slot={slot}
            operands={operands}
            onInsert={insert}
            onCancel={() => setOpen(false)}
          />
        </div>
      )}
      {varsOpen && varsContext && (
        <div
          className="mt-1 border border-emerald-100 rounded p-2 bg-emerald-50/40"
          data-testid={`varsref-panel-${slot}${idSuffix}`}
        >
          <p className="text-[10px] text-gray-500">
            Values a <span className="font-mono">set</span> bound earlier in this workflow. One
            run only — not the fleet's shared reference data.
          </p>
          {varsNote && <p className="mt-1 text-xs text-amber-800">{varsNote}</p>}
          {varsOffers === null && !varsNote && (
            <p className="mt-1 text-xs text-gray-400 animate-pulse">Loading…</p>
          )}
          {varsOffers !== null && varsOffers.length === 0 && !varsNote && (
            <p className="mt-1 text-xs text-gray-400">
              Nothing is in scope here yet. Add a <span className="font-mono">set</span> task
              before this one.
            </p>
          )}
          <ul className="mt-1 space-y-0.5">
            {(varsOffers ?? []).map(o => (
              <li key={o.name}>
                <button
                  type="button"
                  onClick={() => { insertToken(o.token); setVarsOpen(false) }}
                  className="text-xs text-left hover:underline"
                >
                  <span className="font-mono text-emerald-800">{o.token}</span>
                  {o.bound_by && <span className="ml-1 text-gray-400">from {o.bound_by}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
